import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import { of } from 'rxjs/observable/of';

import {
  AccountProfileResponse,
  ItsrApiService,
  MixpanelService,
  SessionStorageService,
  SessionTimeoutService
} from '../_services';
import { MixpanelTrackedFormComponent } from '../global';

@Component({
  templateUrl: 'login.component.html',
})
export class LoginComponent extends MixpanelTrackedFormComponent implements OnInit, OnDestroy {
  data: any = null;
  showLoadingIcon: boolean = false;
  formSuccess: boolean | null = null;
  displayError: string = '';

  activateSuccessMsg: string | null = null;
  usernameValueChangesSubscription: Subscription;
  passwordValueChangesSubscription: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionTimeoutService: SessionTimeoutService,
    private itsrApiService: ItsrApiService,
    private sessionState: SessionStorageService,
    private titleService: Title,
    formBuilder: FormBuilder,
    mixpanelService: MixpanelService
  ) {
    super(
      formBuilder.group({
        username: [null, Validators.compose([Validators.required])],
        password: [null, Validators.compose([Validators.required])],
      }),
      mixpanelService
    );

    this.clearActivateSuccessMsg = this.clearActivateSuccessMsg.bind(this);
  }

  ngOnInit(): void {
    this.data = this.route.snapshot.data;
    this.mixpanelService.trackPageView(this.data.content.windowTitle);
    this.mixpanelService.timeEvent(MixpanelService.EventNameAccountLogin);
    this.titleService.setTitle(this.data.content.windowTitle);

    if ('activated' in this.route.snapshot.queryParams) {
      this.activateSuccessMsg = this.data.content.activateSuccessMessage;
      this.usernameValueChangesSubscription = this.form.get('username').valueChanges
        .subscribe(this.clearActivateSuccessMsg);
      this.passwordValueChangesSubscription = this.form.get('password').valueChanges
        .subscribe(this.clearActivateSuccessMsg);
    }
  }

  ngOnDestroy(): void {
    this.clearActivateSuccessMsg();
  }

  submit(event: Event): void {
    event.preventDefault();

    this.clearActivateSuccessMsg();

    if (!this.isFormGroupValid()) {
      this.markAllFormFieldsTouched();
      this.setFocusOnFirstInvalidField();
      return;
    }

    this.showLoadingIcon = true;
    const { username, password } = this.form.value;
    const subscription = this.itsrApiService.getJwtToken(username, password)
      .mergeMap((response) => {
        if (ItsrApiService.isErrorResponse(response)) {
          return of(response);
        }

        this.sessionState.setAuthData(response.body);
        return this.itsrApiService.getAccountProfile().map((profileResponse) => {
          return ItsrApiService.isErrorResponse(profileResponse)
            ? profileResponse
            : { response, profileResponse };
        });
      })
      .subscribe(
        (response): void => {
          if (ItsrApiService.isErrorResponse(response)) {
            this.sessionState.clearAuthData();

            this.formSuccess = false;
            this.displayError = response.Errors[0].Message;
            this.showLoadingIcon = false;

            return;
          }

          this.mixpanelService.track(MixpanelService.EventNameAccountLogin);
          this.mixpanelTrackLoginDateTime();

          const profileInfo = response.profileResponse.body;
          this.mixpanelIdentifyUserAndTrackProfileInfo(profileInfo);
          this.persistProfileInfoInSessionState(profileInfo);

          this.router.navigate(['/dash']);
          this.sessionTimeoutService.startTimer(); // IMPORTANT: after navigation
        },
        undefined,
        (): void => { subscription.unsubscribe(); }
      );
  }

  private clearActivateSuccessMsg(): void {
    this.activateSuccessMsg = null;
    if (this.usernameValueChangesSubscription) {
      this.usernameValueChangesSubscription.unsubscribe();
    }
    if (this.passwordValueChangesSubscription) {
      this.passwordValueChangesSubscription.unsubscribe();
    }
  }

  private persistProfileInfoInSessionState(profileInfo: AccountProfileResponse): void {
    const subClassId = (profileInfo.ProductList.length > 0 && profileInfo.ProductList[0].ProductSubClassID) || 0;
    this.sessionState.setProductSubClassId(String(subClassId));
    this.sessionState.setUserFullName(`${profileInfo.FirstName} ${profileInfo.LastName}`);
  }

  private mixpanelTrackLoginDateTime(): void {
    const currentDateTimeUTC = new Date().toUTCString();
    this.mixpanelService.setPeopleProperty({
      [MixpanelService.PropertyNameFirstLoginDateTimeUTC]: currentDateTimeUTC
    }, {
      once: true
    });
    this.mixpanelService.setPeopleProperty({
      [MixpanelService.PropertyNameLastLoginDateTimeUTC]: currentDateTimeUTC
    });
  }

  private mixpanelIdentifyUserAndTrackProfileInfo(profileInfo: AccountProfileResponse): void {
    if (profileInfo.ProductList.length > 0) {
      const product = profileInfo.ProductList[0];

      // associate user's registration number with Mixpanel-generate distinct_id
      if (typeof product.Regno === 'string') {
        this.mixpanelService.identify(product.Regno);
      }

      // track the product plan name the user is currently enrolled for
      if (typeof product.ProductSubClassID === 'number') {
        const planName: string = this.sessionState.getProductPlanName(product.ProductSubClassID) ||
          `Unknown Subclass (${product.ProductSubClassID})`;
        this.mixpanelService.setPeopleProperty({
          [MixpanelService.PropertyNameSubscriberProductPlan]: planName
        });
      }
    }

    this.mixpanelService.setPeopleProperty({
      [MixpanelService.PropertyNameSubscriberFirstName]: profileInfo.FirstName,
      [MixpanelService.PropertyNameSubscriberLastName]: profileInfo.LastName,
      [MixpanelService.PropertyNameSubscriberEmailAddress]: profileInfo.Email,
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/login.component.ts